import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { apiError, downloadFile } from "@/lib/api";
import { brl, fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { FileText, Loader2, AlertTriangle, ArrowRight, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const STATUS = {
  em_elaboracao: { label: "Em elaboração", cls: "bg-slate-100 text-slate-600" },
  em_conferencia: { label: "Em conferência", cls: "bg-amber-50 text-amber-700" },
  fechada: { label: "Fechada", cls: "bg-sky-50 text-sky-700" },
  aprovada: { label: "Aprovada", cls: "bg-emerald-50 text-emerald-700" },
};
const NEXT = { em_elaboracao: { to: "em_conferencia", label: "Enviar p/ conferência", perm: "statement.generate" }, em_conferencia: { to: "fechada", label: "Fechar", perm: "statement.close" }, fechada: { to: "aprovada", label: "Aprovar", perm: "statement.approve" } };

const money = (v) => brl(v || 0);

function Section({ title, children }) {
  return <div className="mb-5"><h3 className="font-heading font-semibold text-brand text-base mb-2 border-b border-slate-200 pb-1">{title}</h3>{children}</div>;
}
function SimpleTable({ head, rows }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-slate-400 py-1">Sem registros.</p>;
  return (
    <table className="w-full text-sm mb-2"><thead><tr className="text-xs uppercase text-slate-400">{head.map((h, i) => <th key={i} className={`py-1 ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-slate-100">{r.map((c, j) => <td key={j} className={`py-1.5 ${j === 0 ? "text-left text-slate-700" : "text-right font-mono"}`}>{c}</td>)}</tr>)}</tbody></table>
  );
}

function StatementDocument({ data, full, userName }) {
  const r = data.resumo;
  return (
    <div>
      <div style={{ borderBottom: "3px solid #123826" }} className="flex items-center justify-between pb-3 mb-4">
        <div><div className="text-xl font-heading font-extrabold text-brand tracking-wide">CLUBE MONTE LÍBANO</div><div className="text-xs font-semibold text-gold uppercase tracking-widest">Prestação de Contas</div></div>
        <div className="text-right text-xs text-slate-500">Competência: <strong>{data.competencia}</strong><br />Regime: Caixa<br />{userName ? `Responsável: ${userName}` : ""}</div>
      </div>

      <Section title="1 — Resumo Executivo">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2">
          {[["Saldo Inicial", r.saldo_inicial], ["Receitas", r.receitas], ["Despesas", r.despesas], ["Resultado", r.resultado], ["Saldo Final", r.saldo_final]].map(([l, v], i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-2"><div className="text-[10px] uppercase text-slate-400">{l}</div><div className={`font-mono font-bold ${l === "Despesas" ? "text-rose-600" : l === "Receitas" ? "text-emerald-600" : "text-brand"}`}>{money(v)}</div></div>
          ))}
        </div>
        <SimpleTable head={["Indicador", "Valor"]} rows={[
          ["Contas a pagar vencidas", money(r.pagar_vencidas)], ["Contas a receber vencidas", money(r.receber_vencidas)],
          ["Compromissos próx. 30 dias", money(r.compromissos_30)], ["Recebimentos previstos próx. 30 dias", money(r.recebimentos_30)],
          ["Transferências entre contas (informativo, não afeta o resultado)", money(r.transferencias)],
        ]} />
      </Section>

      <Section title={full ? "2 — Receitas por Categoria" : "Receitas por Categoria"}><SimpleTable head={["Categoria", "Valor"]} rows={data.receitas_categoria.map((x) => [x.name, money(x.valor)])} /></Section>

      {full && <>
        <Section title="3 — Despesas por Categoria"><SimpleTable head={["Categoria", "Valor"]} rows={data.despesas_categoria.map((x) => [x.name, money(x.valor)])} /></Section>
        <Section title="4 — Despesas por Centro de Custo"><SimpleTable head={["Centro de Custo", "Valor"]} rows={data.despesas_centro.map((x) => [x.name, money(x.valor)])} /></Section>
        <Section title="5 — Saldos Bancários e Disponibilidades"><SimpleTable head={["Conta", "Saldo Inicial", "Entradas", "Saídas", "Saldo Final"].map((h, i) => i === 0 ? h : h)} rows={data.saldos_bancarios.map((x) => [x.nome, money(x.saldo_inicial), money(x.entradas), money(x.saidas), money(x.saldo_final)])} /></Section>
        <Section title="6 — Orçado x Realizado (ano)"><SimpleTable head={["Métrica", "Valor"]} rows={[["Orçado", money(data.orcado_realizado.orcado)], ["Realizado", money(data.orcado_realizado.realizado)], ["Diferença", money(data.orcado_realizado.diferenca)], ["% Executado", `${data.orcado_realizado.percent}%`]]} /></Section>
        <Section title="7 — Principais Fornecedores"><SimpleTable head={["Fornecedor", "Qtd", "Valor"]} rows={data.fornecedores.map((x) => [x.name, x.qtd, money(x.valor)])} /></Section>
        <Section title="8 — Transferências entre Contas (informativo)"><SimpleTable head={["Origem → Destino", "Valor"]} rows={data.transferencias_lista.map((x) => [`${x.from} → ${x.to}`, money(x.amount)])} /></Section>
        <Section title="9 — Anexo de Lançamentos do Período"><SimpleTable head={["Data", "Tipo/Descrição", "Valor"]} rows={data.anexo.map((x) => [fmtDate(x.date), `${x.tipo} — ${x.description} (${x.categoria})`, money(x.valor)])} /></Section>
      </>}

      <Section title={full ? "10 — Pendências" : "Pendências"}>
        {data.pendencias.length === 0 ? <p className="text-sm text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Nenhuma pendência crítica encontrada.</p>
          : <SimpleTable head={["Pendência", "Descrição", "Valor"]} rows={data.pendencias.map((p) => [p.tipo, p.descricao, money(p.valor)])} />}
      </Section>
    </div>
  );
}

export default function StatementsPage() {
  const { hasPermission, user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [periodType, setPeriodType] = useState("mensal");
  const [quarter, setQuarter] = useState(1);
  const [semester, setSemester] = useState(1);
  const [preview, setPreview] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [versions, setVersions] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [full, setFull] = useState(true);
  const [attachComprovantes, setAttachComprovantes] = useState(true);
  const printRef = useRef(null);

  const periodParams = () => {
    if (periodType === "mensal") return { year, month, period_type: "mensal", period_index: month };
    if (periodType === "trimestral") return { year, period_type: "trimestral", period_index: quarter };
    if (periodType === "semestral") return { year, period_type: "semestral", period_index: semester };
    return { year, period_type: "anual", period_index: 1 };
  };
  const periodLabel = () => {
    if (periodType === "mensal") return `${MONTHS[month - 1]}/${year}`;
    if (periodType === "trimestral") return `${quarter}º Trimestre/${year}`;
    if (periodType === "semestral") return `${semester}º Semestre/${year}`;
    return `Exercício ${year}`;
  };
  const periodQuery = () => new URLSearchParams(periodParams()).toString();

  const loadVersions = () => api.get(`/statements?${periodQuery()}`).then((res) => setVersions(res.data)).catch(() => {});
  useEffect(() => { loadVersions(); setPreview(null); setViewing(null); /* eslint-disable-next-line */ }, [year, month, periodType, quarter, semester]);

  const check = async () => {
    setLoadingPrev(true);
    try { const { data } = await api.post("/statements/check", periodParams()); setPreview(data); }
    catch (err) { toast.error(apiError(err)); } finally { setLoadingPrev(false); }
  };
  const generate = async () => {
    try { await api.post("/statements/generate", periodParams()); toast.success("Prestação gerada."); loadVersions(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const openVersion = async (v) => {
    try { const { data } = await api.get(`/statements/${v.id}`); setViewing(data); }
    catch (err) { toast.error(apiError(err)); }
  };
  const transition = async (v) => {
    const nx = NEXT[v.status]; if (!nx) return;
    if (nx.to === "fechada" && !window.confirm("Fechar esta prestação? Após fechada, alterações nos dados do período gerarão alerta de desatualização.")) return;
    if (nx.to === "aprovada" && !window.confirm("Aprovar esta prestação de contas?")) return;
    try { await api.post(`/statements/${v.id}/transition`, { status: nx.to }); toast.success("Status atualizado."); loadVersions(); if (viewing?.id === v.id) openVersion(v); }
    catch (err) { toast.error(apiError(err)); }
  };
  const exportExcel = async () => {
    if (!viewing) return;
    try { await downloadFile(`/statements/${viewing.id}/export.xlsx`, `prestacao_${viewing.competencia}.xlsx`); }
    catch (err) { toast.error("Falha ao exportar Excel."); }
  };
  const exportPdf = async () => {
    if (!viewing) return;
    const variant = full ? "completa" : "executiva";
    const anexos = full && attachComprovantes ? "1" : "0";
    if (full && attachComprovantes) toast.info("Gerando PDF com os comprovantes anexados — pode levar alguns segundos.");
    try { await downloadFile(`/statements/${viewing.id}/export.pdf?variant=${variant}&anexos=${anexos}`, `prestacao_${viewing.competencia}_${variant}.pdf`); }
    catch (err) { toast.error("Falha ao gerar PDF."); }
  };

  const canGenerate = hasPermission("statement.generate");

  return (
    <div className="space-y-6">
      <PageHeader title="Prestação de Contas" subtitle="Documento mensal gerado a partir dos dados reais do ERP (regime de caixa)." />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-end gap-3" data-testid="statement-controls">
        <div><label className="block text-xs text-slate-500 mb-1">Período</label>
          <select value={periodType} onChange={(e) => setPeriodType(e.target.value)} data-testid="statement-period-type" className="border border-slate-200 rounded-lg px-3 py-2 text-sm">
            <option value="mensal">Mensal</option>
            <option value="trimestral">Trimestral</option>
            <option value="semestral">Semestral</option>
            <option value="anual">Anual</option>
          </select></div>
        {periodType === "mensal" && (
          <div><label className="block text-xs text-slate-500 mb-1">Mês</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="statement-month" className="border border-slate-200 rounded-lg px-3 py-2 text-sm">{MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</select></div>
        )}
        {periodType === "trimestral" && (
          <div><label className="block text-xs text-slate-500 mb-1">Trimestre</label>
            <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} data-testid="statement-quarter" className="border border-slate-200 rounded-lg px-3 py-2 text-sm">
              <option value={1}>1º (Jan–Mar)</option><option value={2}>2º (Abr–Jun)</option><option value={3}>3º (Jul–Set)</option><option value={4}>4º (Out–Dez)</option>
            </select></div>
        )}
        {periodType === "semestral" && (
          <div><label className="block text-xs text-slate-500 mb-1">Semestre</label>
            <select value={semester} onChange={(e) => setSemester(Number(e.target.value))} data-testid="statement-semester" className="border border-slate-200 rounded-lg px-3 py-2 text-sm">
              <option value={1}>1º (Jan–Jun)</option><option value={2}>2º (Jul–Dez)</option>
            </select></div>
        )}
        <div><label className="block text-xs text-slate-500 mb-1">Ano</label>
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="statement-year" className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-28" /></div>
        <button onClick={check} data-testid="statement-check" className="border border-brand text-brand font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2">{loadingPrev ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />} Verificar pendências</button>
        {canGenerate && <button onClick={generate} data-testid="statement-generate" className="bg-brand hover:bg-brand-hover text-white font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2"><FileText className="h-4 w-4" /> Gerar Prestação</button>}
      </div>

      {preview && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="statement-preview">
          <h3 className="font-heading font-semibold text-slate-900 mb-2">Prévia — {preview.competencia}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            {[["Saldo Inicial", preview.resumo.saldo_inicial], ["Receitas", preview.resumo.receitas], ["Despesas", preview.resumo.despesas], ["Resultado", preview.resumo.resultado], ["Saldo Final", preview.resumo.saldo_final]].map(([l, v], i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-2"><div className="text-[10px] uppercase text-slate-400">{l}</div><div className="font-mono font-bold text-brand">{money(v)}</div></div>
            ))}
          </div>
          {preview.pendencias.length === 0
            ? <p className="text-sm text-emerald-600 flex items-center gap-1" data-testid="preview-no-pendencias"><CheckCircle2 className="h-4 w-4" /> Nenhuma pendência crítica.</p>
            : <div data-testid="preview-pendencias"><p className="text-sm text-amber-700 font-medium mb-1 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> {preview.pendencias.length} pendência(s) — revise antes de fechar (nada é corrigido automaticamente):</p>
              <ul className="text-sm text-slate-600 list-disc pl-6">{preview.pendencias.slice(0, 20).map((p, i) => <li key={i}>{p.tipo}: {p.descricao} {p.valor ? `(${money(p.valor)})` : ""}</li>)}</ul></div>}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 font-heading font-semibold text-slate-900">Versões — {periodLabel()}</div>
        {versions.length === 0 ? <EmptyState text="Nenhuma prestação gerada para este período." />
          : <table className="w-full text-sm" data-testid="statement-versions"><tbody>
            {versions.map((v) => (
              <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`statement-row-${v.id}`}>
                <td className="px-4 py-3 font-medium text-slate-800">v{v.version}</td>
                <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-1 rounded-full ${STATUS[v.status]?.cls}`}>{STATUS[v.status]?.label}</span>{v.outdated && <span className="ml-2 text-xs text-rose-600 font-medium">⚠ Desatualizada</span>}</td>
                <td className="px-4 py-3 text-slate-400 text-xs">Gerada por {v.generated_by_name} em {fmtDate((v.generated_at || "").slice(0, 10))}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openVersion(v)} data-testid={`statement-open-${v.id}`} className="text-brand hover:underline text-sm mr-3">Abrir</button>
                  {NEXT[v.status] && hasPermission(NEXT[v.status].perm) && <button onClick={() => transition(v)} data-testid={`statement-next-${v.id}`} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-brand">{NEXT[v.status].label} <ArrowRight className="h-3.5 w-3.5" /></button>}
                </td>
              </tr>
            ))}
          </tbody></table>}
      </div>

      {viewing && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="statement-document">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                <button onClick={() => setFull(false)} data-testid="doc-executiva" className={`px-3 py-1.5 text-sm ${!full ? "bg-brand text-white" : "bg-white text-slate-600"}`}>Executiva</button>
                <button onClick={() => setFull(true)} data-testid="doc-completa" className={`px-3 py-1.5 text-sm ${full ? "bg-brand text-white" : "bg-white text-slate-600"}`}>Completa</button>
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${STATUS[viewing.status]?.cls}`}>{STATUS[viewing.status]?.label} • v{viewing.version}</span>
              {viewing.outdated && <span className="text-xs text-rose-600 font-medium flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Dados mudaram — gere nova versão</span>}
            </div>
            {hasPermission("reports.export") && <div className="flex items-center gap-2">
              <button onClick={exportExcel} data-testid="statement-excel" className="border border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" /> Exportar Excel</button>
              <button onClick={exportPdf} data-testid="statement-pdf" className="bg-brand hover:bg-brand-hover text-white font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2" title={full ? "A versão Completa anexa os comprovantes das despesas pagas no período ao final do PDF" : "Documento executivo resumido (sem comprovantes anexados)"}><FileText className="h-4 w-4" /> Exportar PDF ({full ? "Completa" : "Executiva"})</button>
            </div>}
          </div>
          {full && (
            <div className="flex items-center gap-2 -mt-2 mb-3" data-testid="comprovantes-hint">
              <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={attachComprovantes} onChange={(e) => setAttachComprovantes(e.target.checked)} className="accent-brand" data-testid="attach-comprovantes-check" />
                Anexar comprovantes das despesas ao final do PDF
              </label>
              <span className="text-xs text-slate-400">(desmarque para um PDF mais leve e rápido)</span>
            </div>
          )}
          <div ref={printRef}><StatementDocument data={viewing.snapshot} full={full} userName={viewing.generated_by_name} /></div>
        </div>
      )}
    </div>
  );
}
